/* eslint-disable */
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, getDocs, deleteDoc, updateDoc, writeBatch, query, collection, where, orderBy, arrayUnion } from "firebase/firestore";
import { auth, db } from "../firebase";
import { COLOR, FONT, TEXT, RADIUS, SPACING, SHADOW } from "../utils/theme";
import { Icon } from "../utils/icons";
import Toast, { useToast } from "./Toast";
import { getCostRange } from "../utils/costHelpers";
import { stripUndefined } from "../utils/helpers";
import { getSetting, getModelTimeout } from "../utils/settings";
import { countryToFlag } from "../utils/countryFlags";
import { CATEGORY_COLORS } from "./constants";
import { writeDaysAndStartGeneration } from "../utils/tripCreation";
import { authenticatedFetch } from "../utils/apiClient";

const API_URL = process.env.REACT_APP_PROXY_URL || "http://localhost:3001";

// Map transitMode values to icon names in ICON_MAP
const TRANSIT_ICON = {
  plane: "transport",
  train: "train",
  bus: "bus",
  ferry: "ferry",
  drive: "drive",
};

// Format YYYY-MM-DD as readable date
function formatDate(dateStr) {
  const d = new Date(dateStr + "T12:00:00Z");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}

// Simple string hash for deterministic copy selection
const simpleHash = s => s.split('').reduce((a, c) => a + c.charCodeAt(0), 0);

// Wave 2 transition copy options
const WAVE2_COPY = [
  "Your route is set. Now for the non-negotiables. ✦",
  "Route locked. Let's build the highlight reel. ✦",
  "Your path is set. Now \u2014 what can't you miss? ✦",
  "Route approved. Time to find your signature moments. ✦",
  "Your route is set. Now for the good part. ✦",
  "Route locked. Let's find your anchor moments. ✦",
  "Your path is approved. Ready to find the highlights? ✦",
  "Route set. Now let's find what's worth the flight. ✦",
  "Your route is locked. Time to curate the essentials. ✦",
  "Route approved. What are we actually doing there? ✦",
];

// Wave 3 transition copy options
const WAVE3_COPY = [
  "Highlights locked. Now let\u2019s fill in the full picture. ✦",
  "Your must-sees are set. Time to build each day. ✦",
  "Anchor moments saved. Now \u2014 the rest of your days. ✦",
  "Highlights approved. Let\u2019s turn them into a real itinerary. ✦",
  "Must-sees locked. Ready to sketch the full days? ✦",
  "Your highlights are in. Now let\u2019s connect the dots. ✦",
  "Anchors locked. Let\u2019s build the days around them. ✦",
  "Highlights saved. Time to fill in the mornings and evenings. ✦",
  "Must-sees approved. Let\u2019s weave them into full days. ✦",
  "Your anchors are set. Ready for the full itinerary? ✦",
];

// CSS-only pulsing dots animation + orbit animation
const LOADING_CSS = `
@keyframes pulseDot {
  0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
}
@keyframes orbit {
  from { transform: rotate(0deg) translateX(34px) rotate(0deg); }
  to   { transform: rotate(360deg) translateX(34px) rotate(-360deg); }
}
@keyframes centerPulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
@keyframes skeletonPulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
`;

function LoadingDots() {
  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            width: 8, height: 8, borderRadius: "50%",
            background: COLOR.mauve,
            animation: `pulseDot 1.4s ease-in-out ${i * 0.2}s infinite`,
          }}
        />
      ))}
    </div>
  );
}

export default function TripSkeletonApproval({ tripStatus, onApproved }) {
  const { tripId: slug } = useParams();
  const navigate = useNavigate();

  const [trip, setTrip] = useState(null);
  const [tripLoading, setTripLoading] = useState(true);
  const [tripError, setTripError] = useState(null);

  const [skeleton, setSkeleton] = useState(null); // { tripTitle, days }
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [approving, setApproving] = useState(false);

  const [editTitle, setEditTitle] = useState("");
  const [editingDayIdx, setEditingDayIdx] = useState(null);
  const [editCity, setEditCity] = useState("");
  const editInputRef = useRef(null);

  // Phase state machine: wave1_approval | wave2_transition | wave2_loading | wave2_approval | wave3_transition | complete
  const [phase, setPhase] = useState(() => {
    if (tripStatus === "wave2_complete") return "wave3_transition";
    if (tripStatus === "wave1_complete") return "wave2_transition";
    return "wave1_approval";
  });

  // Correct phase once trip doc loads — handles "creating" + wave3Started edge case
  useEffect(() => {
    if (!trip) return;
    if (trip.status === "creating" && trip.wave3Started && phase === "wave1_approval") {
      setPhase("wave3_transition");
    }
  }, [trip]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wave 2 state
  const [wave2Days, setWave2Days] = useState([]);
  const [removed, setRemoved] = useState({}); // key: `${dayIndex}-${actIndex}`
  const [wave2Saving, setWave2Saving] = useState(false);
  const [generatingDay, setGeneratingDay] = useState(null); // dayNumber currently fetching, or null

  // Wave 2 city dirty-state tracking: original cities snapshot (never mutated after init)
  const [originalCities, setOriginalCities] = useState({}); // { [dayId]: city }

  const [toast, showToast] = useToast();

  // Deterministic Wave 2 transition copy
  const wave2CopyLine = useMemo(
    () => WAVE2_COPY[Math.abs(simpleHash(slug || "")) % WAVE2_COPY.length],
    [slug]
  );

  // Approved days for Wave 2 (from skeleton)
  const approvedDays = useMemo(
    () => skeleton?.days || [],
    [skeleton]
  );

  // Deterministic Wave 3 transition copy
  const wave3CopyLine = useMemo(
    () => WAVE3_COPY[Math.abs(simpleHash((slug || "") + "w3")) % WAVE3_COPY.length],
    [slug]
  );

  // Auto-navigate from wave3_transition after 2 seconds (Change 2a)
  useEffect(() => {
    if (phase !== 'wave3_transition') return;
    const timer = setTimeout(() => {
      window.location.replace(`/trip/${slug}`);
    }, 2000);
    return () => clearTimeout(timer);
  }, [phase, navigate, slug]);

  // Wave 2 must-sees with Firestore IDs reconstructed for Wave 3
  const wave2ForWave3 = useMemo(() => {
    const result = [];
    wave2Days.forEach((day, dayIdx) => {
      let order = 0;
      (day.mustSees || []).forEach((mustSee, actIdx) => {
        const key = `${dayIdx}-${actIdx}`;
        if (removed[key]) return;
        order++;
        result.push({
          ...mustSee,
          id: `${slug}-day-${day.dayNumber}-act-${order}`,
          dayNumber: day.dayNumber,
        });
      });
    });
    return result;
  }, [wave2Days, removed, slug]);

  // 1. Load trip doc
  useEffect(() => {
    async function loadTrip() {
      setTripLoading(true);
      setTripError(null);
      try {
        const snap = await getDoc(doc(db, "trips", slug));
        if (!snap.exists()) {
          setTripError("Trip not found.");
          return;
        }
        setTrip({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error("[TripSkeletonApproval] Failed to load trip:", e);
        setTripError(e.message);
      } finally {
        setTripLoading(false);
      }
    }
    loadTrip();
  }, [slug]);

  // 1b. Rehydrate days (and must-sees) from Firestore on reload
  useEffect(() => {
    if (!trip || !["wave1_complete", "wave2_complete"].includes(tripStatus)) return;

    const rehydrateDays = async () => {
      const daysSnap = await getDocs(
        query(
          collection(db, "days"),
          where("ownerUid", "==", auth.currentUser.uid),
          where("tripId", "==", trip.id),
          orderBy("order", "asc")
        )
      );

      const firestoreDays = daysSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const rehydratedSkeleton = firestoreDays.map(d => ({
        dayNumber: d.order,
        city: d.city,
        country: d.country,
        date: d.date || d.displayDate,
        transitMode: d.transitMode || null,
        routingRationale: d.routingRationale || null,
        climate: d.climate || null,
        isTransitDay: d.isTransitDay || false,
        flag: d.flag || "\u{1F30D}",
        id: d.id,
      }));

      setSkeleton({ days: rehydratedSkeleton });
      // Snapshot original cities for dirty-state tracking (Change 1a)
      const oc = {};
      rehydratedSkeleton.forEach(d => { oc[`${slug}-day-${d.dayNumber}`] = d.city; });
      setOriginalCities(oc);

      if (tripStatus === "wave2_complete") {
        const actsSnap = await getDocs(
          query(
            collection(db, "activities"),
            where("ownerUid", "==", auth.currentUser.uid),
            where("tripId", "==", trip.id),
            where("aiGenerated", "==", true)
          )
        );

        const mustSeesByDay = {};
        actsSnap.docs.forEach(doc => {
          const act = { id: doc.id, ...doc.data() };
          const day = firestoreDays.find(d => d.id === act.dayId);
          if (!day) return;
          const dn = day.order;
          if (!mustSeesByDay[dn]) {
            mustSeesByDay[dn] = { dayNumber: dn, city: day.city, mustSees: [] };
          }
          mustSeesByDay[dn].mustSees.push(act);
        });

        setWave2Days(Object.values(mustSeesByDay));
      }
    };

    rehydrateDays();
  }, [trip, tripStatus]);

  // 2. Auto-generate skeleton when trip.status === "creating" (new trips only, not Wave 3)
  useEffect(() => {
    if (!trip || skeleton || generating) return;
    if (trip.status === "creating" && !trip.wave3Started) {
      generateSkeleton();
    }
  }, [trip]);

  const generateSkeleton = useCallback(async () => {
    if (!trip?.brief) return;
    setGenerating(true);
    setGenError(null);
    try {
      const brief = trip.brief;
      const res = await authenticatedFetch(`${API_URL}/api/generate-trip-skeleton`, {
        method: "POST",
        body: JSON.stringify({
          destination: brief.destinations?.join(", ") || brief.title,
          departureCity: brief.departureCity || "",
          startDate: brief.departureDate,
          endDate: brief.returnDate,
          travelers: brief.travelers?.join(", ") || "",
          style: brief.style || "",
          budget: brief.budget || "",
          mustSees: brief.mustSees?.join(", ") || "",
          avoid: brief.avoid?.join(", ") || "",
          extendedCaching: getSetting("extendedCaching"),
          language: getSetting('wanderologie_settings.language') || 'English',
          model: getSetting('wanderologie_settings.aiModel'),
        }),
      });
      if (res.status === 429) {
        const errData = await res.json();
        setGenError(errData.message);
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        if (data.error?.code === "refusal") {
          setGenError("Couldn't generate route — try rephrasing your trip details");
        } else {
          throw new Error(data.error?.message || `Server error (${res.status})`);
        }
        return;
      }
      setSkeleton({ tripTitle: data.tripTitle, days: data.days });
      setEditTitle(data.tripTitle);
      // Snapshot original cities for dirty-state tracking (Change 1a)
      const oc = {};
      (data.days || []).forEach(d => { oc[`${slug}-day-${d.dayNumber}`] = d.city; });
      setOriginalCities(oc);
    } catch (e) {
      console.error("[TripSkeletonApproval] Generation failed:", e);
      setGenError(e.message);
    } finally {
      setGenerating(false);
    }
  }, [trip]);

  // 3. Inline edit: update city on a day
  const handleCityEdit = (idx) => {
    setEditingDayIdx(idx);
    setEditCity(skeleton.days[idx].city);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const commitCityEdit = () => {
    if (editingDayIdx === null) return;
    const trimmed = editCity.trim();
    if (trimmed && trimmed !== skeleton.days[editingDayIdx].city) {
      setSkeleton(prev => ({
        ...prev,
        days: prev.days.map((d, i) => i === editingDayIdx ? { ...d, city: trimmed } : d),
      }));
    }
    setEditingDayIdx(null);
  };

  // 4. Approve Wave 1: batch-write days to Firestore, then transition to Wave 2
  const handleApprove = async () => {
    if (!skeleton || approving) return;
    setApproving(true);
    try {
      await writeDaysAndStartGeneration({
        slug,
        skeleton: { ...skeleton, tripTitle: editTitle.trim() || skeleton.tripTitle },
        brief: trip?.brief || {},
        apiUrl: API_URL,
        wave2Days: wave2ForWave3,
        skipWizard: false,
      });

      setPhase("wave2_transition");
    } catch (e) {
      console.error("[TripSkeletonApproval] Approve failed:", e);
      setGenError("Failed to save \u2014 " + e.message);
    } finally {
      setApproving(false);
    }
  };

  // 5. Generate must-sees (Wave 2) — per-day progressive
  const generateMustSees = useCallback(async () => {
    const brief = trip?.brief || {};
    const daysList = approvedDays.map(d => ({
      dayNumber: d.dayNumber,
      city: d.city,
      country: d.country,
      date: d.date,
      isTransitDay: d.isTransitDay || false,
    }));

    // Seed wave2Days with empty mustSees so UI renders immediately
    setWave2Days(daysList.map(d => ({ dayNumber: d.dayNumber, city: d.city, country: d.country || "", mustSees: [] })));
    setRemoved({});
    setPhase("wave2_approval");

    const existingMustSeeTitles = [];

    for (const day of daysList) {
      setGeneratingDay(day.dayNumber);
      console.time(`[CLIENT] must-sees-day-${day.city}`);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), getModelTimeout('skeleton'));

      try {
        const res = await authenticatedFetch(`${API_URL}/api/generate-must-sees-day`, {
          method: "POST",
          signal: controller.signal,
          body: JSON.stringify({ tripBrief: brief, day, existingMustSeeTitles, language: getSetting('wanderologie_settings.language') || 'English', model: getSetting('wanderologie_settings.aiModel') }),
        });

        if (res.status === 429) {
          const errData = await res.json();
          console.error(`[TripSkeletonApproval] Usage limit reached:`, errData.message);
          break; // stop generating remaining days
        }

        const data = await res.json();

        if (!res.ok) {
          console.error(`[TripSkeletonApproval] must-sees-day failed for day ${day.dayNumber} (${day.city}):`, data.error?.message || res.status);
          continue; // leave mustSees: [] for this day
        }

        setWave2Days(prev => prev.map(d =>
          d.dayNumber === data.dayNumber
            ? { ...d, mustSees: data.mustSees || [], city: data.city || d.city }
            : d
        ));

        // Accumulate titles for dedup on subsequent days
        (data.mustSees || []).forEach(ms => {
          if (ms.title) existingMustSeeTitles.push(ms.title);
        });
      } catch (e) {
        if (e.name === "AbortError") {
          console.warn(`[TripSkeletonApproval] must-sees-day timeout for day ${day.dayNumber} (${day.city})`);
        } else {
          console.error(`[TripSkeletonApproval] must-sees-day error for day ${day.dayNumber} (${day.city}):`, e.message);
        }
        // leave mustSees: [] for this day, continue
      } finally {
        clearTimeout(timeout);
        console.timeEnd(`[CLIENT] must-sees-day-${day.city}`);
      }
    }

    setGeneratingDay(null);
  }, [trip, approvedDays]);

  // 6. Approve Wave 2: write activities to Firestore
  const handleWave2Approve = async () => {
    if (wave2Saving) return;
    setWave2Saving(true);
    try {
      const batch = writeBatch(db);
      let totalWritten = 0;

      wave2Days.forEach((day, dayIdx) => {
        const dayNumber = day.dayNumber;
        let order = 0;
        (day.mustSees || []).forEach((mustSee, actIdx) => {
          const key = `${dayIdx}-${actIdx}`;
          if (removed[key]) return; // skip removed
          order++;
          const actId = `${slug}-day-${dayNumber}-act-${order}`;
          const actDoc = stripUndefined({
            id: actId,
            tripId: slug,
            ownerUid: auth.currentUser.uid,
            dayId: `${slug}-day-${dayNumber}`,
            planType: "primary",
            order: order,
            aiGenerated: true,
            locked: false,
            confirmed: false,
            version: 1,
            timeBlock: mustSee.timeBlock || null,
            img: null,
            imgCropParams: null,
            notes: null,
            alternative: null,
            bookingConfirmationRef: null,
            previousVersion: null,
            previousBookingStatus: null,
            title: mustSee.title,
            detail: mustSee.detail || null,
            category: mustSee.category || "leisure",
            cost: mustSee.cost ?? null,
            costRange: getCostRange(mustSee.cost),
            duration: mustSee.duration || null,
            bookingStatus: mustSee.bookingStatus || "not_needed",
            bookingLeadDays: mustSee.bookingLeadDays ?? null,
            aiRationale: mustSee.aiRationale || null,
            location: mustSee.location || null,
            bookingUrl: mustSee.bookingUrl || null,
            bookingNote: mustSee.bookingNote || null,
            costDetail: mustSee.costDetail || null,
          });
          batch.set(doc(db, "activities", actId), actDoc);
          totalWritten++;
        });
      });

      // Collect titles of removed highlights for brief.avoid
      const removedTitles = [];
      wave2Days.forEach((day, dayIdx) => {
        (day.mustSees || []).forEach((ms, actIdx) => {
          if (removed[`${dayIdx}-${actIdx}`] && ms.title) {
            removedTitles.push(`Declined suggestion: ${ms.title}`);
          }
        });
      });

      // Update trip status (+ append removed highlights to brief.avoid atomically)
      batch.update(doc(db, "trips", slug), {
        status: "wave2_complete",
        ...(removedTitles.length > 0 ? { "brief.avoid": arrayUnion(...removedTitles) } : {}),
      });

      await batch.commit();

      // Write dirty city changes to Firestore (Change 1d)
      const dirtyOverrides = {};
      wave2Days.forEach(day => {
        const did = `${slug}-day-${day.dayNumber}`;
        const cur = (day.city || "").trim();
        if (originalCities[did] && cur && cur !== originalCities[did]) {
          dirtyOverrides[did] = cur;
        }
      });
      if (Object.keys(dirtyOverrides).length > 0) {
        try {
          const cityBatch = writeBatch(db);
          Object.entries(dirtyOverrides).forEach(([did, city]) => {
            cityBatch.update(doc(db, "days", did), { city });
          });
          await cityBatch.commit();
        } catch (cityErr) {
          console.error("[TripSkeletonApproval] City update failed:", cityErr);
          showToast("Failed to save city changes \u2014 please try again.", "error");
          setWave2Saving(false);
          return; // do not advance phase
        }
      }


      // Set wave3Started + creating status so ItineraryView picks up generation
      try {
        await updateDoc(doc(db, "trips", slug), { status: "creating", wave3Started: true });
      } catch (err) {
        console.error("Wave3 status update failed:", err);
      }

      // Generation is triggered by ItineraryView when it detects wave3Started + creating.
      // Do NOT fire generateTripActivities here — the page is about to be destroyed
      // by wave3_transition's window.location.replace, which kills in-flight async.

      showToast("✦ Highlights locked in!", "success");
      setPhase("wave3_transition");
    } catch (e) {
      console.error("[TripSkeletonApproval] Wave 2 save failed:", e);
      showToast("Failed to save highlights \u2014 " + e.message, "error");
    } finally {
      setWave2Saving(false);
    }
  };




  // ─── Render ────────────────────────────────────────────────

  const destination = trip?.brief?.destinations?.join(", ") || trip?.brief?.title || "your trip";
  const dateRange = trip?.brief
    ? `${formatDate(trip.brief.departureDate)} \u2013 ${formatDate(trip.brief.returnDate)}`
    : "";

  // Loading trip doc
  if (tripLoading) {
    return (
      <div style={{ minHeight: "100vh", background: COLOR.cream, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{LOADING_CSS}</style>
        <LoadingDots />
      </div>
    );
  }

  // Trip doc error
  if (tripError) {
    return (
      <div style={{ minHeight: "100vh", background: COLOR.cream, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <Icon name="warning" size={20} color={COLOR.mauveDark} />
        <span style={{ ...TEXT.body, color: COLOR.mauveDark }}>{tripError}</span>
        <button
          onClick={() => navigate("/")}
          style={{
            background: COLOR.buttonSecondary, border: `1px solid ${COLOR.borderWarm}`,
            borderRadius: RADIUS.lg, color: COLOR.inkMid, padding: "10px 18px",
            cursor: "pointer", fontFamily: FONT.mono, fontSize: 12,
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // ─── Wave 2: Transition screen ───────────────────────────────
  if (phase === "wave2_transition") {
    return (
      <div style={{
        minHeight: "100vh", background: COLOR.cream,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: FONT.serif, color: COLOR.inkDark,
      }}>
        <style>{LOADING_CSS}</style>
        <Toast message={toast.message} type={toast.type} visible={toast.visible} />
        <div style={{
          textAlign: "center", maxWidth: 400, padding: "0 32px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 32,
        }}>
          <div style={{
            fontFamily: FONT.display, fontSize: 22,
            color: COLOR.inkDark, lineHeight: 1.5,
          }}>
            {wave2CopyLine}
          </div>
          <button
            onClick={generateMustSees}
            style={{
              width: "100%", maxWidth: 360,
              background: COLOR.mauve, border: "none",
              borderRadius: RADIUS.lg, color: COLOR.white,
              padding: "16px", fontSize: 16, fontFamily: FONT.mono,
              cursor: "pointer", minHeight: 52, letterSpacing: "0.04em",
              boxShadow: SHADOW.panel, transition: "background 0.2s",
            }}
          >
            Find my highlights {"✦"}
          </button>
        </div>
      </div>
    );
  }

  // ─── Wave 2: Approval screen (also handles legacy "wave2_loading" phase) ────
  if (phase === "wave2_approval" || phase === "wave2_loading") {
    return (
      <div style={{
        minHeight: "100vh", background: COLOR.cream,
        fontFamily: FONT.serif, color: COLOR.inkDark,
      }}>
        <style>{LOADING_CSS}</style>
        <Toast message={toast.message} type={toast.type} visible={toast.visible} />

        <div style={{
          maxWidth: 640, margin: "0 auto",
          padding: `0 ${SPACING.pagePadding}`,
          paddingTop: "max(24px, env(safe-area-inset-top))",
          paddingBottom: 120,
        }}>
          {/* Header */}
          <h1 style={{ ...TEXT.pageTitle, fontWeight: 400, margin: "0 0 4px" }}>
            Your highlights {"✦"}
          </h1>
          <div style={{
            ...TEXT.detail, fontStyle: "italic", marginBottom: SPACING.sectionGap,
          }}>
            These are the anchor moments for your trip. Remove anything that doesn't fit.
          </div>

          {/* Day sections */}
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {wave2Days.map((day, dayIdx) => {
              const dayId = `${slug}-day-${day.dayNumber}`;
              const currentCity = wave2Days[dayIdx].city || "";
              const isDirty = originalCities[dayId] && currentCity !== originalCities[dayId];

              return (
                <div key={day.dayNumber}>
                  {/* Day header with editable city */}
                  <div style={{
                    ...TEXT.label, marginBottom: 4, fontSize: 11,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <span>{countryToFlag(day.country || "")}</span>
                    <span>DAY {day.dayNumber} —</span>
                    <input
                      value={currentCity}
                      onChange={e => {
                        const val = e.target.value;
                        setWave2Days(prev => prev.map((d, i) => i === dayIdx ? { ...d, city: val } : d));
                      }}
                      style={{
                        ...TEXT.label, fontSize: 11, margin: 0,
                        border: "none", borderBottom: `1px solid ${COLOR.borderWarm}`,
                        background: "transparent", outline: "none",
                        padding: "2px 4px", textTransform: "uppercase",
                        fontFamily: FONT.mono, letterSpacing: "0.1em",
                        width: 120,
                      }}
                      onFocus={e => { e.target.style.borderBottomColor = COLOR.mauve; }}
                      onBlur={e => { e.target.style.borderBottomColor = COLOR.borderWarm; }}
                    />
                  </div>
                  {isDirty && (
                    <p style={{ fontSize: 12, color: COLOR.inkMid, marginTop: 4, marginBottom: 6 }}>
                      City updated · activities will be generated for {currentCity}
                    </p>
                  )}

                  {/* Per-day loading / empty states */}
                  {(!day.mustSees || day.mustSees.length === 0) && generatingDay !== null && (
                    <div style={{
                      padding: "18px 16px",
                      background: COLOR.white,
                      border: `1px solid ${COLOR.borderWarm}`,
                      borderRadius: RADIUS.lg,
                      boxShadow: SHADOW.card,
                    }}>
                      <span style={{
                        fontFamily: FONT.serif, fontSize: 12,
                        color: COLOR.inkMid,
                        animation: "skeletonPulse 2s ease-in-out infinite",
                      }}>
                        Finding highlights…
                      </span>
                    </div>
                  )}
                  {(!day.mustSees || day.mustSees.length === 0) && generatingDay === null && (
                    <div style={{
                      padding: "18px 16px",
                      background: COLOR.white,
                      border: `1px solid ${COLOR.borderWarm}`,
                      borderRadius: RADIUS.lg,
                      boxShadow: SHADOW.card,
                    }}>
                      <span style={{
                        fontFamily: FONT.serif, fontSize: 12,
                        color: COLOR.inkMid,
                      }}>
                        No highlights found
                      </span>
                    </div>
                  )}

                  {/* Activity cards */}
                  <div style={{ display: "flex", flexDirection: "column", gap: parseInt(SPACING.cardGap) }}>
                    {(day.mustSees || []).map((mustSee, actIdx) => {
                      const key = `${dayIdx}-${actIdx}`;
                      const isRemoved = !!removed[key];
                      const cat = CATEGORY_COLORS[mustSee.category] || CATEGORY_COLORS.leisure;

                      return (
                        <div
                          key={key}
                          style={{
                            position: "relative",
                            background: COLOR.white,
                            border: `1px solid ${COLOR.borderWarm}`,
                            borderRadius: RADIUS.lg,
                            padding: "14px 16px",
                            boxShadow: SHADOW.card,
                            opacity: isRemoved ? 0.4 : 1,
                            transition: "opacity 0.2s",
                          }}
                        >
                          {/* Remove / Undo button */}
                          <button
                            onClick={() => setRemoved(prev => ({ ...prev, [key]: !isRemoved }))}
                            style={{
                              position: "absolute", top: 8, right: 8,
                              background: "none", border: "none",
                              cursor: "pointer", padding: "4px 8px",
                              display: "flex", alignItems: "center", gap: 4,
                              color: isRemoved ? COLOR.mauve : COLOR.inkMid,
                              fontFamily: FONT.mono, fontSize: 10,
                              letterSpacing: "0.04em",
                              minWidth: 44, minHeight: 44,
                              justifyContent: "flex-end",
                            }}
                          >
                            {isRemoved ? (
                              <><Icon name="unbook" size={12} color={COLOR.mauve} /> Undo</>
                            ) : (
                              <><Icon name="close" size={12} color={COLOR.inkMid} /> Remove</>
                            )}
                          </button>

                          {/* Category badge */}
                          <div style={{
                            display: "inline-flex", alignItems: "center",
                            background: cat.bg, color: cat.text,
                            padding: "2px 8px", borderRadius: RADIUS.sm,
                            fontFamily: FONT.mono, fontSize: 9,
                            letterSpacing: "0.06em", marginBottom: 6,
                          }}>
                            {cat.label}
                          </div>

                          {/* Title */}
                          <div style={{
                            ...TEXT.cardTitle, fontWeight: 400,
                            textDecoration: isRemoved ? "line-through" : "none",
                            paddingRight: 60, marginBottom: 4,
                          }}>
                            {mustSee.title}
                          </div>

                          {/* Detail (2 lines max) */}
                          {mustSee.detail && (
                            <div style={{
                              ...TEXT.detail, marginBottom: 8,
                              display: "-webkit-box",
                              WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}>
                              {mustSee.detail}
                            </div>
                          )}

                          {/* Meta row: cost + booking status + duration */}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: mustSee.aiRationale ? 8 : 0 }}>
                            {mustSee.cost != null && (
                              <span style={{
                                fontFamily: FONT.mono, fontSize: 11, color: COLOR.inkDark,
                              }}>
                                {mustSee.costRange || getCostRange(mustSee.cost)}
                              </span>
                            )}
                            {mustSee.duration && (
                              <span style={{
                                fontFamily: FONT.mono, fontSize: 10, color: COLOR.inkMid,
                              }}>
                                {mustSee.duration}
                              </span>
                            )}
                            {mustSee.bookingStatus && mustSee.bookingStatus !== "not_needed" && (
                              <span style={{
                                fontFamily: FONT.mono, fontSize: 9,
                                background: COLOR.blush, color: COLOR.mauve,
                                padding: "2px 6px", borderRadius: RADIUS.sm,
                                letterSpacing: "0.04em",
                              }}>
                                {mustSee.bookingStatus === "required" ? "Booking required" :
                                 mustSee.bookingStatus === "recommended" ? "Booking recommended" :
                                 mustSee.bookingStatus}
                              </span>
                            )}
                          </div>

                          {/* AI rationale */}
                          {mustSee.aiRationale && (
                            <div style={{
                              ...TEXT.detail, fontStyle: "italic",
                              color: COLOR.inkLight || COLOR.inkMid,
                              fontSize: 11, lineHeight: 1.4,
                            }}>
                              {mustSee.aiRationale}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>


                </div>
              );
            })}
          </div>
        </div>

        {/* Sticky continue button */}
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: `linear-gradient(transparent, ${COLOR.cream} 20%)`,
          padding: "24px 24px max(16px, env(safe-area-inset-bottom))",
          display: "flex", justifyContent: "center",
        }}>
          <button
            data-testid="wave2-approve"
            onClick={handleWave2Approve}
            disabled={wave2Saving || generatingDay !== null}
            style={{
              width: "100%", maxWidth: 640,
              background: (wave2Saving || generatingDay !== null) ? COLOR.borderWarm : COLOR.mauve,
              border: "none", borderRadius: RADIUS.lg,
              color: COLOR.white, padding: "16px",
              fontSize: 16, fontFamily: FONT.mono,
              cursor: (wave2Saving || generatingDay !== null) ? "default" : "pointer",
              minHeight: 52, letterSpacing: "0.04em",
              boxShadow: SHADOW.panel, transition: "background 0.2s",
            }}
          >
            {generatingDay !== null ? "Finding highlights…" : wave2Saving ? "Saving..." : "Continue ✦"}
          </button>
        </div>
      </div>
    );
  }

  // ─── Wave 3: Transition screen ───────────────────────────────
  if (phase === "wave3_transition") {
    return (
      <div style={{
        minHeight: "100vh", background: COLOR.cream,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: FONT.serif, color: COLOR.inkDark,
      }}>
        <style>{LOADING_CSS}</style>
        <Toast message={toast.message} type={toast.type} visible={toast.visible} />
        <div style={{
          textAlign: "center", maxWidth: 400, padding: "0 32px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 32,
        }}>
          <div style={{
            fontFamily: FONT.display, fontSize: 22,
            color: COLOR.inkDark, lineHeight: 1.5,
          }}>
            {wave3CopyLine}
          </div>
          {/* Auto-advance progress bar (Change 2b) */}
          <div style={{
            width: "100%", maxWidth: 360, height: 3,
            borderRadius: RADIUS.pill, background: COLOR.borderWarm,
            overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: RADIUS.pill,
              background: COLOR.mauve,
              width: "100%",
              animation: "wave3Fill 2s linear forwards",
            }} />
          </div>
          <style>{`@keyframes wave3Fill { from { width: 0%; } to { width: 100%; } }`}</style>
        </div>
      </div>
    );
  }

  // ─── Wave 2: Complete ────────────────────────────────────────
  if (phase === "complete") {
    return (
      <div style={{
        minHeight: "100vh", background: COLOR.cream,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <style>{LOADING_CSS}</style>
        <Toast message={toast.message} type={toast.type} visible={toast.visible} />
        <LoadingDots />
      </div>
    );
  }

  // ─── Wave 1: Approval screen (original) ─────────────────────

  return (
    <div data-testid="skeleton-approval" style={{
      minHeight: "100vh", background: COLOR.cream,
      fontFamily: FONT.serif, color: COLOR.inkDark,
    }}>
      <style>{LOADING_CSS}</style>
      <Toast message={toast.message} type={toast.type} visible={toast.visible} />

      <div style={{
        maxWidth: 640, margin: "0 auto",
        padding: `0 ${SPACING.pagePadding}`,
        paddingTop: "max(24px, env(safe-area-inset-top))",
        paddingBottom: 120,
      }}>
        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          style={{
            background: "none", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 4,
            color: COLOR.inkMid, fontFamily: FONT.mono, fontSize: 12,
            padding: "8px 0", marginBottom: 16,
          }}
        >
          <Icon name="back" size={14} color={COLOR.inkMid} /> Back
        </button>

        {/* Trip title (editable when skeleton loaded) */}
        {skeleton ? (
          <input
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            style={{
              ...TEXT.pageTitle,
              width: "100%", background: "transparent",
              border: "none", borderBottom: `1px solid ${COLOR.borderWarm}`,
              outline: "none", padding: "4px 0", marginBottom: 4,
              fontWeight: 400,
            }}
          />
        ) : (
          <h1 style={{ ...TEXT.pageTitle, fontWeight: 400, margin: "0 0 4px" }}>
            {trip?.title || "New Trip"}
          </h1>
        )}

        {/* Date range */}
        {dateRange && (
          <div style={{ ...TEXT.label, marginBottom: SPACING.sectionGap }}>
            {dateRange}
          </div>
        )}

        {/* Generating state */}
        {generating && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: 16, padding: "60px 0",
          }}>
            <LoadingDots />
            <span style={{
              fontFamily: FONT.display, fontSize: 18,
              color: COLOR.inkMid, textAlign: "center",
            }}>
              Planning your route to {destination}...
            </span>
          </div>
        )}

        {/* Generation error */}
        {genError && !generating && (
          <div style={{
            background: COLOR.blushLight, border: `1px solid ${COLOR.mauve}33`,
            borderRadius: RADIUS.lg, padding: SPACING.cardPadding,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
            marginBottom: 24,
          }}>
            <Icon name="warning" size={18} color={COLOR.mauveDark} />
            <span style={{ ...TEXT.body, color: COLOR.mauveDark, textAlign: "center" }}>
              {genError}
            </span>
            <button
              onClick={async () => {
                try { await deleteDoc(doc(db, "trips", slug)); } catch (e) { console.error("[TripSkeletonApproval] Failed to delete trip:", e); }
                navigate("/trip/new");
              }}
              style={{
                background: COLOR.mauve, border: "none",
                borderRadius: RADIUS.lg, color: COLOR.white,
                padding: "12px 24px", cursor: "pointer",
                fontFamily: FONT.mono, fontSize: 13,
              }}
            >
              Try again
            </button>
          </div>
        )}

        {/* Skeleton day list */}
        {skeleton && !generating && (
          <div style={{ display: "flex", flexDirection: "column", gap: parseInt(SPACING.cardGap) }}>
            {skeleton.days.map((d, i) => {
              const isTransit = d.isTransitDay;
              const isEditing = editingDayIdx === i;
              const transitIcon = TRANSIT_ICON[d.transitMode];

              return (
                <div
                  key={d.dayNumber}
                  data-testid="skeleton-day-card"
                  style={{
                    background: isTransit ? COLOR.blushLight : COLOR.white,
                    border: `1px solid ${COLOR.borderWarm}`,
                    borderRadius: RADIUS.lg, padding: "14px 16px",
                    boxShadow: SHADOW.card,
                    cursor: "pointer",
                    transition: "box-shadow 0.15s",
                  }}
                  onClick={() => !isEditing && handleCityEdit(i)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {/* Day number badge */}
                    <div style={{
                      width: 36, height: 36, borderRadius: RADIUS.md,
                      background: isTransit ? COLOR.blush : COLOR.buttonSecondary,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <span style={{ fontFamily: FONT.mono, fontSize: 13, color: COLOR.inkDark, fontWeight: 600 }}>
                        {d.dayNumber}
                      </span>
                    </div>

                    {/* Main content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                        {/* City (editable) */}
                        {isEditing ? (
                          <input
                            ref={editInputRef}
                            value={editCity}
                            onChange={e => setEditCity(e.target.value)}
                            onBlur={commitCityEdit}
                            onKeyDown={e => { if (e.key === "Enter") commitCityEdit(); if (e.key === "Escape") setEditingDayIdx(null); }}
                            onClick={e => e.stopPropagation()}
                            style={{
                              ...TEXT.cardTitle,
                              fontWeight: 400, border: "none",
                              borderBottom: `2px solid ${COLOR.mauve}`,
                              background: "transparent", outline: "none",
                              padding: "2px 0", width: "100%",
                            }}
                          />
                        ) : (
                          <span style={{ ...TEXT.cardTitle, fontWeight: 400 }}>
                            {countryToFlag(d.country)} {d.city}
                          </span>
                        )}
                      </div>

                      {/* Date + transit badge */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ ...TEXT.label, margin: 0 }}>
                          {formatDate(d.date)}
                        </span>
                        {isTransit && transitIcon && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            background: `${COLOR.mauve}15`,
                            padding: "2px 8px", borderRadius: RADIUS.pill,
                          }}>
                            <Icon name={transitIcon} size={12} color={COLOR.mauve} />
                            <span style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.mauve, letterSpacing: "0.06em" }}>
                              {d.transitMode.toUpperCase()}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* Routing rationale */}
                      <div style={{ ...TEXT.detail, marginTop: 6, lineHeight: 1.5 }}>
                        {d.routingRationale}
                      </div>

                      {/* Climate note */}
                      {d.climate && (
                        <div style={{ fontSize: 12, color: COLOR.inkMid, fontFamily: FONT.serif, marginTop: 5, lineHeight: 1.5, fontStyle: "italic" }}>
                          {d.climate}
                        </div>
                      )}
                    </div>

                    {/* Edit hint */}
                    {!isEditing && (
                      <Icon name="edit" size={14} color={COLOR.dragHandle} style={{ flexShrink: 0 }} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky approve button */}
      {skeleton && !generating && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: `linear-gradient(transparent, ${COLOR.cream} 20%)`,
          padding: "24px 24px max(16px, env(safe-area-inset-bottom))",
          display: "flex", justifyContent: "center",
        }}>
          <button
            data-testid="wave1-approve"
            onClick={handleApprove}
            disabled={approving}
            style={{
              width: "100%", maxWidth: 640,
              background: approving ? COLOR.borderWarm : COLOR.mauve,
              border: "none", borderRadius: RADIUS.lg,
              color: COLOR.white, padding: "16px",
              fontSize: 16, fontFamily: FONT.mono,
              cursor: approving ? "default" : "pointer",
              minHeight: 52, letterSpacing: "0.04em",
              boxShadow: SHADOW.panel,
              transition: "background 0.2s",
            }}
          >
            {approving ? "Saving..." : "Looks good ✦"}
          </button>
        </div>
      )}
    </div>
  );
}
